<template>
  <div>
    <modal size="mdsm"
           class="security-modal"
           :visible="securityModalVisible"
           prevent-backdrop-close
           :close-button-visible="false"
           @modal-close="handleModalClose">
      <div slot="header" class="header-close">
        <button class="header-close__button"
                tabindex="-1"
                @click="closeModal">
          <img src="/assets/images/modal-close.svg" alt="">
        </button>
      </div>
      <div class="modal-body">
        <img class="logo" src="/assets/images/logo.svg" alt=""/>
        <h2 class="title">{{ $t('securityCodePopup.title') }}</h2>
        <div class="security-code">
          <validation-observer ref="securityCodeObs"
                               tag="form"
                               class="code-form"
                               v-slot="{ errors }"
                               @submit.prevent="confirmSecurityCode"
                               v-autofocus>
            <label for="security-1" class="code-label">{{ $t('userAccount.securityCode.enterCodeLabel') }}</label>
            <div class="code-inputs-wrapper">
              <validation-provider v-for="index in inputFieldsCount"
                                   :key="'provider'+index"
                                   tag="div"
                                   class="code-input"
                                   :mode="formSubmitted ? 'eager' : 'passive'"
                                   v-slot="{ errors }"
                                   :vid="'security-'+index"
                                   rules="required|numeric|max:1">
                <base-input v-model="securityCode[index - 1]"
                            :invalid="!!errors.length"
                            maxlength="1"
                            :ref="'input'+index"
                            :id="'security-'+index"
                            :name="'security-'+index"
                            class="security-code-input"
                            @input="handleCodeInput(index, $event)"/>
              </validation-provider>
            </div>
            <p class="error-msg" v-if="formSubmitted && isFormInvalid(errors) && numberOfAttempts === 1">{{$t('securityCodePopup.wrongCodeError1')}}</p>
            <p class="error-msg" v-else-if="formSubmitted && isFormInvalid(errors) && numberOfAttempts === 2">{{$t('securityCodePopup.wrongCodeError2')}}</p>
            <div class="action-button-wrapper">
              <base-button type="submit"
                           variant="primary"
                           ref="submitBtn"
                           class="submit-button"
                           :disabled="enteredCode.length < 6 || dataLoading || formInvalid">
                {{ $t('securityCodePopup.submitCodeButton') }}
              </base-button>
            </div>
          </validation-observer>
        </div>
      </div>
    </modal>
    <modal size="mdsm"
           class="security-modal"
           :visible="securityRecoveryModalVisible"
           prevent-backdrop-close
           :close-button-visible="false"
           @modal-close="closeRecoveryModal">
      <div slot="header" class="header-close">
        <button class="header-close__button"
                tabindex="-1"
                @click="closeModal">
          <img src="/assets/images/modal-close.svg" alt="">
        </button>
      </div>
      <div class="modal-body">
        <img class="logo" src="/assets/images/logo.svg" alt=""/>
        <h2 class="title">{{ $t('securityCodePopup.title') }}</h2>
        <p class="recovery-modal-body">{{ $t('securityCodePopup.securityCodeRecoveryBody') }}</p>
      </div>
    </modal>
  </div>
</template>

<script>
import { customRouter } from '@/customRouter'
import Modal from '@/components/common/modal/modal'
import codeInput from '@/mixins/codeInput'
import { SECURITY_COOKIE_NAME, SECURITY_COOKIE_VALIDITY, LOGIN_COOKIE_NAME, SECURITY_MODAL_TIMEOUT } from '@/services/securityCookieHandler'
import { differenceInSeconds } from 'date-fns'
import logout from '@/mixins/logout'
import { lsSave, lsRemove, lsGet } from '@/utils/localStorageController'
import errorMessageHandler from '@/mixins/errorMessageHandler'

export default {
  name: 'security-code-check',
  data () {
    return {
      securityModalVisible: false,
      securityRecoveryModalVisible: false,
      inputFieldsCount: 6,
      securityCode: [],
      formSubmitted: false,
      dataLoading: false,
      securityTimeout: null,
      numberOfAttempts: 0
    }
  },
  components: { Modal },
  mixins: [codeInput, logout, errorMessageHandler],
  computed: {
    enteredCode () {
      return this.securityCode.join('')
    }
  },
  methods: {
    fetchUserInfo () {
      this.$apiRequest.get('user/info')
        .then(response => {
          this.handleSecurityCode(response.data.securityCodeActive)
          this.$emit('security-code-ok', true)
        })
        .catch(e => {
          console.log(e.response.data)
          if (e.response.data && e.response.data.errors && e.response.data.errors[0].type && e.response.data.errors[0].type === 'MtmSecurityCodeError') {
            this.initSecurityTimer(0)
          } else if (e.response.data && e.response.data.errors && e.response.data.errors[0].type && e.response.data.errors[0].type === 'MtmCustomerInfoUpdateNotCompleteError') {
            this.handleErrorMessage(e.response.data)
          }
        })
    },
    showSecurityModal () {
      this.$emit('security-code-ok', false)
      this.securityModalVisible = true
    },
    initSecurityTimer (timeout = SECURITY_MODAL_TIMEOUT) {
      this.securityTimeout = null
      this.securityTimeout = setTimeout(() => {
        this.showSecurityModal()
        clearTimeout(this.securityTimeout)
        this.securityTimeout = null
      }, timeout)
    },
    setSecurityCookie () {
      // TODO Maybe add some encryption to timestamp?
      const currentTime = new Date().getTime()
      this.$cookies.set(SECURITY_COOKIE_NAME, currentTime, SECURITY_COOKIE_VALIDITY)
    },
    handleSecurityCode (hasSecurityEnabled = false) {
      if (hasSecurityEnabled) {
        if (this.$cookies.isKey(LOGIN_COOKIE_NAME) && !this.$cookies.isKey(SECURITY_COOKIE_NAME)) {
          // console.log('just logged in')
          // has just logged in, set security cookie here
          this.setSecurityCookie()
          this.initSecurityTimer()
          setTimeout(() => {
            this.$cookies.remove(LOGIN_COOKIE_NAME)
          }, 10)
        }

        if (!this.$cookies.isKey(LOGIN_COOKIE_NAME) && !this.$cookies.isKey(SECURITY_COOKIE_NAME)) {
          // console.log('deleted security cookie')
          // user deleted security cookie, show modal
          this.setSecurityCookie()
          this.initSecurityTimer(0)
        }

        if (!this.$cookies.isKey(LOGIN_COOKIE_NAME) && this.$cookies.isKey(SECURITY_COOKIE_NAME)) {
          // console.log('usual case')
          const cookieTime = +this.$cookies.get(SECURITY_COOKIE_NAME) // needs to be a number
          const parsed = differenceInSeconds(new Date(), new Date(cookieTime))
          // const calculatedTimeout =
          const timeDifference = Math.max(SECURITY_MODAL_TIMEOUT - (parsed * 1000), 0) // if the diff is < 0, show modal now
          // console.log(timeDifference)
          this.initSecurityTimer(timeDifference)
        }
        if (this.$refs.securityCodeObs) {
          this.$refs.securityCodeObs.reset()
        }
      } else {
        if (this.$cookies.isKey(LOGIN_COOKIE_NAME)) this.$cookies.remove(LOGIN_COOKIE_NAME)
      }
    },
    handleModalClose () {
      this.securityModalVisible = false
      // this.logoutUser()
    },
    closeModal () {
      this.handleModalClose()
      this.logoutUser()
    },
    closeRecoveryModal () {
      this.securityRecoveryModalVisible = false
      this.logoutUser()
    },
    async confirmSecurityCode () {
      const isValid = await this.$refs.securityCodeObs.validate()
      this.formSubmitted = true

      if (isValid) {
        this.submitSecurityCode()
      }
    },
    submitSecurityCode () {
      this.dataLoading = true

      this.$apiRequest.post('user/securitycode/confirm', { code: this.enteredCode })
        .then(response => {
          // handle code confirmation
          if (response.data === 200) {
            this.securityModalVisible = false
            this.securityCode = []
            this.setSecurityCookie()
            this.initSecurityTimer()
            this.$emit('security-code-ok', true)
            this.dataLoading = false
            lsRemove('securityCodeAttempts')
          }
        })
        .catch(e => {
          // handle code error
          const errorsObj = {}
          Object.keys(this.$refs.securityCodeObs.fields).forEach(key => {
            errorsObj[key] = 'error'
          })
          this.$refs.securityCodeObs.setErrors(errorsObj)
          this.formInvalid = true
          this.dataLoading = false
          const attempts = lsGet('securityCodeAttempts') || 0
          lsSave('securityCodeAttempts', Number(attempts) + 1)
          this.numberOfAttempts = attempts
          this.numberOfAttempts++

          if (this.numberOfAttempts === 3) {
            this.securityModalVisible = false
            this.securityRecoveryModalVisible = true
          }
        })
    }
  },
  created () {
    if (!customRouter.isPublicRoute()) {
      this.fetchUserInfo()
    } else {
      this.$emit('security-code-ok', true)
    }

    // ovo tek treba testirati da li radi, posto nisam uspeo sa BE da dobijem ovu gresku
    this.$root.$on('enter-security-code', () => {
      if (!this.securityModalVisible) {
        this.initSecurityTimer(0)
      }
    })
  }
}
</script>
<style scoped lang="scss" src="../userAccount/styles/security-code.scss"></style>
<style scoped lang="scss">
.modal-body {
  padding: 0;

  @media (map_get($breakpoints, sm)) {
    padding: 0 4rem;
  }

  .recovery-modal-body{
    margin-top: 2.4rem;
    text-align: center;
    font-weight:500;
  }

  .logo {
    display: block;
    width: 100%;
    max-width: 160px;
    height: auto;
    margin: auto;
  }

  .title {
    margin: 2.4rem 0 1.5rem;
    text-align: center;
    color: $text;
    font-size: 2.4rem;

    @media (map_get($breakpoints, sm)) {
      margin: 5.6rem 0 0.4rem;
    }
  }

  .code-label, .error-msg {
    @media (map_get($breakpoints, sm)) {
      padding-left: 1rem;
    }
  }

  .code-inputs-wrapper {
    justify-content: space-between;
    @media (map_get($breakpoints, sm)) {
      justify-content: center;
    }
  }

  .action-button-wrapper .submit-button {
    display: block;
    width: 100%;

    @media (map_get($breakpoints, sm)) {
      width: calc(100% - 2rem);
      margin: 0 1rem;
    }
  }
}

.header-close {
  text-align: right;

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;

    img {
      opacity: 1;
      position: relative;
      width: 100%;
      height: auto;
      pointer-events: none;
      transition: opacity 150ms;
    }

    &:hover {

      img {
        opacity: .8;
      }
    }

    &:active img {
      opacity: 1;
    }
  }
}
</style>
